/**
 * Temporary screen-scoped design tokens for the **Signers ("Add signers")** step
 * of `/create/request?type=bulkSend`.
 *
 * Mirrors `Bulk Send.dc.html` 1:1 — every colour, size, radius, spacing and type
 * value the screen needs lives here so the components never inline a literal.
 * Once every screen is re-skinned these get promoted into `src/theme` and this
 * file is deleted (see UI_IMPLEMENTATION_RULES.md § Temporary Screen Tokens).
 */

import { inkfree, notification, signer, white } from "theme/colors";

/** The raw palette the design draws from. */
const palette = {
  ink: signer.ink,
  body: signer.text.secondary,
  muted: signer.text.faint,
  borderLight: signer.border.light,
  borderMain: signer.disabled,
  borderStrong: signer.border.strong,
  surface: signer.surface.muted,
  surfaceAlt: signer.surface.subtle,
  white,
  accent: signer.orange.main,
  accentHover: signer.orange.dark,
  /**
   * Solid CTA fill — per the standing brand-colour rule this is the app
   * brand red, not `accent` above, which stays orange for text/borders.
   */
  ctaBg: inkfree.main,
  ctaBgHover: inkfree.dark,
  accentTint: signer.orange.tint,
  accentBorder: signer.orange.border,
  accentSoft: signer.orange.faintest,
  accentDisabled: "#F0AE95",
  accentChipHover: signer.orange.progressTrack,
  green: "#3AA76D",
  greenText: "#2E8757",
  greenTint: signer.success.tint,
  greenBorder: "#CBE8D6",
  greenTintHover: "#DDF2E6",
  danger: "#D6455D",
  dangerBorder: "#F5C6CE",
  dangerTint: notification.error.bg,
  dangerBadgeTint: notification.error.bg,
  rowErrorBg: signer.orange.faintest,
  purple: "#8B5CF6",
  purpleTint: "#F6F1FE",
  purpleBorder: "#E4D8F9",
  divider: signer.surface.muted,
} as const;

export const bulkSendSignersTokens = {
  palette,

  /** Content column wrapper. */
  page: {
    maxWidth: 1080,
    padding: "32px 40px 80px",
  },

  /** Step 1 — Upload Document ("Select documents to be signed"). */
  upload: {
    padding: "56px 40px 60px",
    titleFontSize: "30px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    titleColor: palette.ink,
    titleMarginBottom: "24px",
    btnRowGap: "12px",
    btnRowMarginBottom: "22px",
    btnGap: "9px",
    btnHeight: 42,
    btnPaddingX: "18px",
    btnRadius: "9px",
    btnBorder: `1.5px solid ${palette.accentBorder}`,
    btnColor: palette.accent,
    btnFontSize: "13.5px",
    btnFontWeight: 700,
    btnHoverBg: palette.accentTint,
    templateIconSize: 16,
    cloudIconSize: 17,
    caretSize: 11,
    menuTop: "48px",
    menuWidth: 190,
    menuBorder: `1px solid ${palette.borderLight}`,
    menuRadius: "10px",
    menuShadow: "0 10px 30px rgba(33,21,71,.12)",
    menuPadding: "6px",
    menuItemPadding: "9px 12px",
    menuItemRadius: "7px",
    menuItemFontSize: "13px",
    menuItemFontWeight: 600,
    menuItemColor: palette.ink,
    menuItemHoverBg: palette.surface,
    dropBorder: `1.5px dashed ${palette.borderMain}`,
    dropRadius: "14px",
    dropPadding: "88px 40px",
    dropGap: "22px",
    dropHoverBorder: palette.accent,
    dropHoverBg: palette.accentSoft,
    dropIconSize: 44,
    dropIconColor: palette.ink,
    dropInnerGap: "7px",
    dropTitleGap: "9px",
    dropTitleFontSize: "19px",
    dropTitleFontWeight: 600,
    dropTitleColor: palette.ink,
    dropInfoIconSize: 15,
    dropInfoIconColor: signer.text.faint,
    dropSubFontSize: "12.5px",
    dropSubColor: palette.muted,
    dropSubLetterSpacing: ".02em",
    listMarginTop: "22px",
    listLabelFontSize: "13px",
    listLabelFontWeight: 700,
    listLabelColor: palette.ink,
    listLabelMarginBottom: "10px",
    chipsGap: "10px",
    chipGap: "8px",
    chipHeight: 36,
    chipPadding: "0 6px 0 10px",
    chipRadius: "8px",
    chipBg: palette.accentTint,
    chipBorder: `1px solid ${palette.accentBorder}`,
    chipMaxWidth: 260,
    chipDotsIconSize: 12,
    chipDotsColor: palette.muted,
    chipNameFontSize: "13px",
    chipNameFontWeight: 600,
    chipNameColor: palette.ink,
    chipRemoveSize: 24,
    chipRemoveRadius: "6px",
    chipRemoveColor: palette.body,
    chipRemoveHoverBg: palette.accentChipHover,
    chipRemoveIconSize: 12,
  },

  /** "Add signers" heading + subtitle + Download example CSV. */
  head: {
    gap: "6px",
    marginBottom: "22px",
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    titleColor: palette.ink,
    subFontSize: "13px",
    subFontWeight: 500,
    subColor: palette.muted,
    downloadHeight: 40,
    downloadPaddingX: "18px",
    downloadRadius: "9px",
    downloadBorder: `1.5px solid ${palette.accentBorder}`,
    downloadFontSize: "13px",
    downloadFontWeight: 700,
    downloadGap: "9px",
    downloadIconSize: 15,
  },

  /** Shared button + chip primitives. */
  btn: {
    radius: "9px",
    fontWeight: 700,
    // Outlined (Import CSV / Download): accent text, accent-tint border.
    outlinedBorder: `1.5px solid ${palette.accentBorder}`,
    outlinedHoverBg: palette.accentTint,
    // Solid primary (Add signer): accent fill.
    solidHoverBg: palette.ctaBgHover,
  },

  /** Sticky summary + actions bar. */
  bar: {
    /** Sits just below the flow's 58px sticky top bar. */
    stickyTop: "58px",
    gap: "8px",
    padding: "10px 12px",
    radius: "10px",
    okBorder: palette.borderLight,
    okBg: palette.white,
    errBorder: palette.dangerBorder,
    errBg: palette.rowErrorBg,
    shadow: "0 4px 14px rgba(33,21,71,.06)",
  },

  /** Filter pills (All / Needs fixing / Valid). */
  filter: {
    gap: "7px",
    height: 30,
    paddingX: "13px",
    radius: "999px",
    fontSize: "12.5px",
    fontWeight: 800,
    iconSize: 12,
    badgeMinWidth: 18,
    badgeHeight: 18,
    badgeRadius: "9px",
    badgePaddingX: "5px",
    badgeFontSize: "10.5px",
    badgeFontWeight: 800,
    allActiveBg: palette.ink,
    allActiveBorder: palette.ink,
    allActiveColor: palette.white,
    allIdleBg: palette.white,
    allIdleBorder: palette.borderMain,
    allIdleColor: palette.body,
    allBadgeBg: "rgba(33,21,71,.08)",
    errActiveBg: palette.danger,
    errActiveBorder: palette.danger,
    errActiveColor: palette.white,
    errIdleBg: palette.white,
    errIdleBorder: palette.dangerBorder,
    errIdleColor: palette.danger,
    errBadgeBg: "rgba(214,69,93,.12)",
    okActiveBg: palette.green,
    okActiveBorder: palette.green,
    okActiveColor: palette.white,
    okIdleBg: palette.white,
    okIdleBorder: palette.greenBorder,
    okIdleColor: palette.greenText,
    okBadgeBg: "rgba(58,167,109,.12)",
  },

  /** "Remove all with errors" pill. */
  removeAll: {
    gap: "6px",
    height: 30,
    paddingX: "12px",
    radius: "8px",
    border: `1px solid ${palette.dangerBorder}`,
    color: palette.danger,
    fontSize: "12px",
    fontWeight: 700,
    hoverBg: palette.dangerTint,
    iconSize: 12,
  },

  /** Import CSV (outlined) + Add signer (solid) actions. */
  action: {
    gap: "8px",
    height: 36,
    paddingX: "15px",
    radius: "9px",
    fontSize: "13px",
    fontWeight: 700,
    importIconSize: 14,
    addIconSize: 13,
  },

  /** Green "N recipients added" banner. */
  imported: {
    gap: "10px",
    padding: "11px 16px",
    radius: "9px",
    bg: palette.greenTint,
    border: `1px solid ${palette.greenBorder}`,
    iconSize: 14,
    iconColor: palette.green,
    fontSize: "12.5px",
    fontWeight: 600,
    color: palette.greenText,
    dismissSize: 24,
    dismissRadius: "6px",
    dismissHoverBg: palette.greenTintHover,
    dismissIconSize: 11,
  },

  /** Dashed empty states. */
  empty: {
    border: `1.5px dashed ${palette.borderMain}`,
    radius: "12px",
    noRecipientsPadding: "44px",
    noRecipientsGap: "14px",
    noRecipientsFontSize: "14px",
    noRecipientsFontWeight: 600,
    noRecipientsColor: palette.body,
    btnGap: "10px",
    btnHeight: 38,
    btnPaddingX: "20px",
    btnRadius: "9px",
    btnFontSize: "13px",
    btnFontWeight: 700,
    filteredPadding: "36px",
    filteredFontSize: "13.5px",
    filteredFontWeight: 600,
    filteredColor: palette.muted,
  },

  /** Recipient rows. */
  rows: {
    gap: "8px",
    rowGap: "14px",
    rowMinHeight: 60,
    rowPadding: "0 14px 0 16px",
    rowInnerGap: "14px",
    rowRadius: "10px",
    rowShadow: "0 1px 3px rgba(33,21,71,.05)",
    okBorder: palette.borderLight,
    okBg: palette.white,
    errBorder: palette.dangerBorder,
    errBg: palette.rowErrorBg,
    numSize: 26,
    numBg: palette.surface,
    numColor: palette.body,
    numFontSize: "12px",
    numFontWeight: 800,
    inputHeight: 38,
    inputUnderline: palette.borderMain,
    inputUnderlineError: palette.danger,
    inputFontSize: "14px",
    nameFontWeight: 600,
    emailFontWeight: 500,
    inputColor: palette.ink,
    inputPlaceholder: palette.muted,
    inputPaddingX: "4px",
    sourceGap: "6px",
    sourceHeight: 24,
    sourcePaddingX: "10px",
    sourceRadius: "999px",
    sourceBg: palette.purpleTint,
    sourceColor: palette.purple,
    sourceFontSize: "10.5px",
    sourceFontWeight: 800,
    sourceMaxWidth: 150,
    sourceIconSize: 10,
    checkSize: 15,
    checkColor: palette.green,
    removeSize: 32,
    removeRadius: "8px",
    removeColor: palette.muted,
    removeHoverBg: palette.accentTint,
    removeHoverColor: palette.accent,
    removeIconSize: 15,
    errorMargin: "5px 0 2px 24px",
    errorGap: "7px",
    errorColor: palette.danger,
    errorIconSize: 12,
    errorFontSize: "12px",
    errorFontWeight: 600,
  },

  /** CSV upload phase. */
  csvUpload: {
    gap: "18px",
    backGap: "7px",
    backColor: palette.body,
    backFontSize: "13px",
    backFontWeight: 700,
    backHoverColor: palette.accent,
    backIconSize: 13,
    dropBorder: `1.5px dashed ${palette.borderMain}`,
    dropRadius: "14px",
    dropPadding: "70px 40px",
    dropGap: "22px",
    dropHoverBorder: palette.accent,
    dropHoverBg: palette.accentSoft,
    dropIconSize: 44,
    dropIconColor: palette.ink,
    dropTitleFontSize: "19px",
    dropTitleFontWeight: 600,
    dropTitleColor: palette.ink,
    dropSubFontSize: "12.5px",
    dropSubColor: palette.muted,
    dropSubLetterSpacing: ".02em",
    dropInnerGap: "7px",
    infoGap: "10px",
    infoPadding: "13px 16px",
    infoRadius: "9px",
    infoBg: palette.surface,
    infoIconSize: 15,
    infoIconColor: palette.body,
    infoFontSize: "12.5px",
    infoFontWeight: 500,
    infoColor: palette.body,
    infoLineHeight: 1.6,
    infoBoldColor: palette.ink,
  },

  /** CSV mapping phase. */
  mapping: {
    gap: "18px",
    fileChipGap: "8px",
    fileChipHeight: 36,
    fileChipPadding: "0 6px 0 12px",
    fileChipRadius: "8px",
    fileChipBg: palette.accentTint,
    fileChipBorder: `1px solid ${palette.accentBorder}`,
    fileChipIconSize: 13,
    fileChipIconColor: palette.accent,
    fileChipNameFontSize: "13px",
    fileChipNameFontWeight: 600,
    fileChipNameColor: palette.ink,
    fileChipRowsFontSize: "11.5px",
    fileChipRowsFontWeight: 600,
    fileChipRowsColor: palette.muted,
    fileChipRemoveSize: 24,
    fileChipRemoveRadius: "6px",
    fileChipRemoveColor: palette.body,
    fileChipRemoveHoverBg: palette.accentChipHover,
    fileChipRemoveIconSize: 12,

    aiGap: "10px",
    aiPadding: "13px 16px",
    aiRadius: "9px",
    aiBg: palette.purpleTint,
    aiBorder: `1px solid ${palette.purpleBorder}`,
    aiIconSize: 16,
    aiIconColor: palette.purple,
    aiFontSize: "12.5px",
    aiFontWeight: 500,
    aiColor: palette.body,
    aiLineHeight: 1.6,
    aiBoldColor: palette.ink,

    cardGap: "12px",
    cardBorder: `1px solid ${palette.borderLight}`,
    cardRadius: "12px",
    cardPadding: "20px",
    cardShadow: "0 1px 3px rgba(33,21,71,.05)",
    mapRowGap: "16px",
    fieldColWidth: 180,
    fieldGap: "8px",
    fieldFontSize: "13.5px",
    fieldFontWeight: 700,
    fieldColor: palette.ink,
    requiredFontSize: "10px",
    requiredFontWeight: 800,
    requiredColor: palette.danger,
    requiredBg: palette.dangerBadgeTint,
    requiredRadius: "5px",
    requiredPadding: "2px 7px",
    requiredLetterSpacing: ".04em",
    arrowIconSize: 16,
    arrowIconColor: palette.borderStrong,
    selGap: "10px",
    selHeight: 38,
    selMinWidth: 220,
    selPaddingX: "12px",
    selRadius: "8px",
    selBorderMapped: palette.borderMain,
    selBorderUnmapped: palette.danger,
    selColorMapped: palette.ink,
    selColorUnmapped: palette.muted,
    selFontSize: "13px",
    selFontWeight: 600,
    selHoverBorder: palette.accent,
    selCaretSize: 10,
    selCaretColor: palette.body,
    menuTop: "44px",
    menuWidth: 240,
    menuBorder: `1px solid ${palette.borderLight}`,
    menuRadius: "10px",
    menuShadow: "0 10px 30px rgba(33,21,71,.14)",
    menuPadding: "6px",
    optionPadding: "9px 12px",
    optionRadius: "7px",
    optionFontSize: "13px",
    optionFontWeight: 600,
    optionColor: palette.ink,
    optionHoverBg: palette.surface,
    optionCheckSize: 13,
    optionCheckColor: palette.accent,
    aiTagGap: "5px",
    aiTagHeight: 24,
    aiTagPaddingX: "10px",
    aiTagRadius: "999px",
    aiTagBg: palette.purpleTint,
    aiTagColor: palette.purple,
    aiTagFontSize: "10.5px",
    aiTagFontWeight: 800,
    aiTagLetterSpacing: ".03em",
    aiTagIconSize: 10,
    sampleFontSize: "12px",
    sampleFontWeight: 500,
    sampleColor: palette.muted,

    previewBorder: `1px solid ${palette.borderLight}`,
    previewRadius: "12px",
    previewHeadPadding: "12px 16px",
    previewHeadBg: palette.surfaceAlt,
    previewHeadBorder: `1px solid ${palette.borderLight}`,
    previewTitleFontSize: "12px",
    previewTitleFontWeight: 800,
    previewTitleColor: palette.body,
    previewTitleLetterSpacing: ".04em",
    previewMetaFontSize: "11.5px",
    previewMetaFontWeight: 600,
    previewMetaColor: palette.muted,

    footerGap: "10px",
    cancelHeight: 42,
    cancelPaddingX: "20px",
    cancelRadius: "9px",
    cancelBorder: `1.5px solid ${palette.borderMain}`,
    cancelColor: palette.body,
    cancelFontSize: "14px",
    cancelFontWeight: 700,
    cancelHoverBorder: palette.muted,
    confirmHeight: 42,
    confirmPaddingX: "22px",
    confirmRadius: "9px",
    confirmReadyBg: palette.accent,
    confirmDisabledBg: palette.accentDisabled,
    confirmColor: palette.white,
    confirmFontSize: "14px",
    confirmFontWeight: 700,
  },
} as const;

export default bulkSendSignersTokens;
